import React from 'react'
import { DollarSign, CreditCard, TrendingUp, RefreshCw, Clock, Activity, CheckCircle } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

export default function OverviewTab({ 
  revenueData, 
  revenueLoading, 
  stripeData, 
  chartData,
  syncStatus 
}) {
  const stats = [
    {
      label: 'Total Revenue',
      value: `$${((revenueData?.total_revenue || 0) / 100).toLocaleString()}`,
      bgGradient: 'from-emerald-500 to-teal-600',
      icon: DollarSign,
      textColor: 'text-white'
    },
    {
      label: 'Paid Invoices',
      value: revenueData?.paid_count || 0,
      bgGradient: 'from-indigo-500 to-purple-600',
      icon: CreditCard,
      textColor: 'text-white'
    },
    {
      label: 'Avg Transaction',
      value: `$${((revenueData?.avg_amount || 0) / 100).toFixed(2)}`,
      bgGradient: 'from-amber-500 to-orange-600',
      icon: TrendingUp,
      textColor: 'text-white'
    },
    {
      label: 'Pending',
      value: revenueData?.pending_count || 0,
      bgGradient: 'from-gray-500 to-slate-600',
      icon: Clock,
      textColor: 'text-white'
    },
  ]

  return (
    <div className="space-y-6">
      {/* Auto-Sync Status Banner */}
      {syncStatus && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${syncStatus.scheduler_running ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                <RefreshCw className={`w-5 h-5 ${syncStatus.scheduler_running ? 'text-green-600' : 'text-yellow-400'}`} />
              </div>
              <div>
                <p className="font-medium text-white">
                  Auto-Sync: {syncStatus.scheduler_running ? (
                    <span className="text-green-600">Active</span>
                  ) : (
                    <span className="text-yellow-400">Inactive</span>
                  )}
                </p>
                <p className="text-sm text-zinc-300">
                  {syncStatus.next_sync && (
                    <>Next sync: {new Date(syncStatus.next_sync).toLocaleString()}</>
                  )}
                </p>
              </div>
            </div>
            <div className="text-right text-sm">
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-zinc-400">Customers</p>
                  <p className="font-bold text-white">{syncStatus.data_counts?.customers || 0}</p>
                </div>
                <div>
                  <p className="text-zinc-400">Charges</p>
                  <p className="font-bold text-white">{syncStatus.data_counts?.charges || 0}</p>
                </div>
                <div>
                  <p className="text-zinc-400">Total Syncs</p>
                  <p className="font-bold text-white">{syncStatus.total_syncs || 0}</p>
                </div>
              </div>
              {syncStatus.last_sync && (
                <p className="text-xs text-zinc-500 mt-1">
                  Last sync: {new Date(syncStatus.last_sync).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat, idx) => (
          <div key={idx} className={`bg-gradient-to-br ${stat.bgGradient} rounded-2xl p-5 ${stat.textColor}`}>
            <div className="flex items-center gap-2 mb-2 opacity-90">
              <stat.icon className="w-5 h-5" />
              <span className="text-sm font-medium">{stat.label}</span>
            </div>
            <p className="text-3xl font-bold">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Revenue Chart */}
      <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
        <h3 className="font-semibold text-white mb-4">Revenue Trend</h3>
        {revenueLoading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          </div>
        ) : chartData.length > 0 ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#a1a1aa' }} />
                <YAxis tick={{ fontSize: 11, fill: '#a1a1aa' }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => [`$${v.toFixed(2)}`, 'Revenue']} />
                <Line type="monotone" dataKey="amount" stroke="#6366f1" strokeWidth={2} dot={{ fill: '#6366f1' }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-64 flex items-center justify-center text-zinc-500">
            No revenue data available
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Success Rate</p>
              <p className="text-xl font-bold text-white">
                {stripeData?.metrics?.success_rate?.toFixed(1) || 0}%
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-green-500/100/20 flex items-center justify-center">
              <Activity className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Active Subscriptions</p>
              <p className="text-xl font-bold text-white">
                {stripeData?.metrics?.active_subscriptions || 0}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-zinc-800 rounded-2xl p-6 border border-zinc-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-zinc-400">Stripe Balance</p>
              <p className="text-xl font-bold text-white">
                ${(stripeData?.balance?.available || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
